/**
 * Pull the eval-conversation ids out of a `send_test_message_to_agent` /
 * `read_eval_conversation` tool result.
 *
 * WHY THIS IS NOT JUST `JSON.parse`. The result the frontend receives is
 * `tool_debug.preview`, which the SSE layer builds as
 * `json.dumps(resp_data, indent=2)[:2000]` (see agent_conversation.py) and
 * which `_persist_tool_calls` then stores verbatim as the call's `result`.
 * Any simulation whose reply runs past that budget — and a normal agent
 * answer does, easily — arrives CUT MID-STRING, so it is not valid JSON.
 * `JSON.parse` throws, and a call site that stops there concludes "no ids"
 * and renders its pill disabled. That is exactly how every test-agent pill
 * in a Hebrew session went dead: the reply was long, the preview was
 * truncated, and the panel had nothing to open.
 *
 * So there are two strategies, in order:
 *
 *   A. Parse as JSON, descending through the wrapper keys the ADK uses
 *      ({"result": "<json>"} / {"response": {...}} / output / data).
 *   B. If that fails or yields no id, scrape the string with a regex.
 *      `eval_conversation_id` is always a UUID and the backend deliberately
 *      orders it FIRST in the payload (`target_reply` is kept last precisely
 *      so the ids survive truncation), so the anchor is present even in a
 *      cut payload.
 *
 * Both call sites — the inline pill in ToolCallBlock and the live stream
 * handler in AoAChatPanel — MUST go through here. They used to carry two
 * copies of this logic; only one of them ever grew the truncation fallback,
 * which is the drift this module exists to prevent.
 *
 * Behaviour is pinned by
 * tests/unit/agent_of_agents/signal_and_eval_result.test.mjs, which asserts
 * the truncated payload is genuinely invalid JSON before checking the ids
 * still come out — so the test cannot pass for the wrong reason.
 */

export interface EvalToolRef {
  eval_conversation_id: string;
  target_chatbot_id?: string;
  target_agent_name?: string;
  target_assistant_message_id?: string;
  target_user_message_id?: string;
}

/** Wrapper keys the ADK has been observed to nest a tool return under. */
const WRAPPER_KEYS = ['result', 'response', 'output', 'data'] as const;

const UUID_GROUP =
  '([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})';

/** Descend through wrapper keys until we find an object carrying the id. */
function unwrap(obj: unknown, depth = 0): Record<string, any> | null {
  if (!obj || typeof obj !== 'object' || depth > 4) return null;
  const rec = obj as Record<string, any>;
  if (rec.eval_conversation_id) return rec;
  for (const key of WRAPPER_KEYS) {
    const inner = rec[key];
    if (inner == null) continue;
    if (typeof inner === 'string') {
      try {
        const found = unwrap(JSON.parse(inner), depth + 1);
        if (found) return found;
      } catch {
        /* truncated or not JSON — strategy B will handle it */
      }
    } else if (typeof inner === 'object') {
      const found = unwrap(inner, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

/** Scrape ids out of a payload too damaged to parse. */
function scrape(raw: string): EvalToolRef | null {
  const evalMatch = raw.match(
    new RegExp(`eval_conversation_id[^a-fA-F0-9]+${UUID_GROUP}`),
  );
  if (!evalMatch) return null;
  const uuid = (field: string): string | undefined => {
    const m = raw.match(new RegExp(`${field}[^a-fA-F0-9]+${UUID_GROUP}`));
    return m ? m[1] : undefined;
  };
  // Permissive on quoting: the string may be escaped (nested JSON) or not.
  const text = (field: string): string | undefined => {
    const m = raw.match(
      new RegExp(`${field}\\\\?"\\s*:\\s*\\\\?"([^"\\\\]+)\\\\?"`),
    );
    return m ? m[1] : undefined;
  };
  return {
    eval_conversation_id: evalMatch[1],
    target_chatbot_id: uuid('target_chatbot_id'),
    target_assistant_message_id: uuid('target_assistant_message_id'),
    target_user_message_id: uuid('target_user_message_id'),
    target_agent_name: text('target_agent_name'),
  };
}

/**
 * Best-effort extraction. Returns null only when the payload genuinely
 * carries no eval conversation — never merely because it was truncated.
 */
export function extractEvalRef(raw: unknown): EvalToolRef | null {
  if (raw == null) return null;

  // Strategy A — structured.
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }
  const found = unwrap(parsed);
  if (found?.eval_conversation_id) {
    return {
      eval_conversation_id: String(found.eval_conversation_id),
      target_chatbot_id: found.target_chatbot_id
        ? String(found.target_chatbot_id)
        : undefined,
      target_agent_name: found.target_agent_name
        ? String(found.target_agent_name)
        : undefined,
      target_assistant_message_id: found.target_assistant_message_id
        ? String(found.target_assistant_message_id)
        : undefined,
      target_user_message_id: found.target_user_message_id
        ? String(found.target_user_message_id)
        : undefined,
    };
  }

  // Strategy B — truncated / escaped / otherwise unparseable.
  if (typeof raw === 'string') return scrape(raw);
  // A non-string payload that failed A can still be scraped once flattened
  // (e.g. an object whose nested string value was cut).
  try {
    return scrape(JSON.stringify(raw));
  } catch {
    return null;
  }
}
